import { OrderConfigurationsResponse, ProbeConfigResponse } from "@fintechprimitives/fpapi";
import { Loader, Toast } from "pixel";
import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { isLicenceCodePOA } from "../../components/functional/OrderOperations/utils";
import { reportErrorToSentry } from "../../global/sentry/sentry";
import { ConditionalRenderer } from "../../utils/helpers";
import { getErrorDescription } from "../../utils/utils";
import { useFpapi } from "../fpapi/useFpapi";
import { useUserInfo } from "../userinfo/useUserInfo";

type DefaultOrderGateway = "rta" | "bse" | "ondc" | "ncdex" | null;
type SupportedGateways = OrderConfigurationsResponse["supported_gateways"] | null;

type GlobalAppStoreContextType = {
  isAmcTenant: boolean;
  supportedGateways: SupportedGateways;
  defaultOrderGateway: DefaultOrderGateway;
  probeConfig: ProbeConfigResponse | null;
  refetchTenantDependentConfig: () => Promise<void>;
};

export const GlobalAppStoreContext = createContext<GlobalAppStoreContextType | null>(null);

const GlobalAppStoreProvider = ({ children }: PropsWithChildren) => {
  const { fpapi } = useFpapi();

  const userInfo = useUserInfo();
  const isUserInfoPresent = userInfo?.data?.userInfo;
  const isUserWithTenants = userInfo?.data?.userInfo?.tenants?.length;

  const [isAmcTenant, setIsAmcTenant] = useState(false);
  const [supportedGateways, setSupportedGateways] = useState<SupportedGateways>(null);
  const [defaultOrderGateway, setDefaultOrderGateway] = useState<DefaultOrderGateway>(null);
  const [probeConfig, setProbeConfig] = useState<ProbeConfigResponse | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTenantDependentConfig = useCallback(async () => {
    try {
      if (!fpapi?.fpDashboardClient) return;

      const orderConfigurationsRes = await fpapi?.fpDashboardClient
        ?.configurations()
        ?.fetchOrderConfigurations();

      setIsAmcTenant(isLicenceCodePOA(orderConfigurationsRes?.ria_code));
      setSupportedGateways(orderConfigurationsRes?.supported_gateways ?? null);
      setDefaultOrderGateway(orderConfigurationsRes?.default_order_gateway ?? null);
    } catch (e: any) {
      const errorMessage = getErrorDescription(e);

      if (e?.status === 400 && ["Invalid Tenant"].includes(errorMessage)) {
        return;
      }

      throw e;
    }
  }, [fpapi?.fpDashboardClient]);

  const fetchTenantIndependentConfig = useCallback(async () => {
    if (!fpapi?.fpDashboardClient) return;

    const probeConfig = await fpapi?.fpDashboardClient?.probe()?.fetchConfig();
    setProbeConfig(probeConfig ?? null);
  }, [fpapi?.fpDashboardClient]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        if (!isUserInfoPresent) {
          return;
        }

        if (isUserWithTenants) {
          await fetchTenantDependentConfig();
        }

        await fetchTenantIndependentConfig();
      } catch (e: any) {
        window.debug.error(e);

        const errorMessage = getErrorDescription(e);

        setError(errorMessage);

        reportErrorToSentry(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [
    fetchTenantDependentConfig,
    fetchTenantIndependentConfig,
    isUserWithTenants,
    isUserInfoPresent,
  ]);

  const value = useMemo(() => {
    return {
      isAmcTenant,
      supportedGateways,
      defaultOrderGateway,
      probeConfig,
      refetchTenantDependentConfig: fetchTenantDependentConfig,
    };
  }, [
    isAmcTenant,
    supportedGateways,
    defaultOrderGateway,
    probeConfig,
    fetchTenantDependentConfig,
  ]);

  if (loading) {
    return <Loader variant="fullpage" />;
  }

  return (
    <GlobalAppStoreContext.Provider value={value}>
      <ConditionalRenderer check={Boolean(error)}>
        <Toast
          message={error || "Error fetching global app store data"}
          variant="error"
          duration={5000}
          onClose={() => setError(null)}
        />
      </ConditionalRenderer>
      {children}
    </GlobalAppStoreContext.Provider>
  );
};

export default GlobalAppStoreProvider;
